import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crosshair, Shield, Clock, TrendingUp, AlertTriangle, Target, Gauge, Lock, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface StrategicFrameworkSectionProps {
  sessionMeta?: Record<string, any> | null;
  impactScore?: number | null;
  urgencyScore?: number | null;
}

export function StrategicFrameworkSection({ sessionMeta, impactScore, urgencyScore }: StrategicFrameworkSectionProps) {
  if (!sessionMeta) return null;

  const thesis = sessionMeta.decision_thesis;
  const commitMode = sessionMeta.phase1_commit_mode;
  const killCriterion = sessionMeta.kill_criterion;
  const irr = sessionMeta.phase1_3_irreversibility;
  const conflict = sessionMeta.phase1_2_conflict;
  const worstCase = sessionMeta.worst_case;
  const worstCaseProb = sessionMeta.worst_case_probability;

  // Only render if we have at least some Phase-1 data
  const hasData = thesis || killCriterion || irr || conflict || impactScore != null || urgencyScore != null;
  if (!hasData) return null;

  const urgencyScoreVal = irr?.structural_urgency_score;
  const getUrgencyColor = (score: number) => {
    if (score >= 7) return "text-destructive";
    if (score >= 4) return "text-amber-500";
    return "text-emerald-500";
  };
  const getUrgencyBg = (score: number) => {
    if (score >= 7) return "bg-destructive/10 border-destructive/20";
    if (score >= 4) return "bg-amber-500/10 border-amber-500/20";
    return "bg-emerald-500/10 border-emerald-500/20";
  };

  return (
    <Card id="strategic-framework" className="border-primary/20 scroll-mt-24">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Crosshair className="h-5 w-5 text-primary" />
          Strategischer Rahmen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Decision Thesis */}
        {thesis && (
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/15">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Entscheidungsthese</p>
              {commitMode && (
                <Badge variant="outline" className={cn(
                  "text-[10px]",
                  commitMode === 'commit' 
                    ? "bg-primary/10 text-primary border-primary/30" 
                    : "bg-muted text-muted-foreground"
                )}>
                  {commitMode === 'commit' ? 'Commit-Modus' : 'Explore-Modus'}
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium italic leading-relaxed">
              „{thesis.thesis || thesis.kernsatz || (typeof thesis === 'string' ? thesis : '')}"
            </p>
            {thesis.core_assumptions && Array.isArray(thesis.core_assumptions) && thesis.core_assumptions.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase">Kernannahmen</p>
                {thesis.core_assumptions.slice(0, 3).map((a: string, i: number) => (
                  <p key={i} className="text-xs text-muted-foreground">• {a}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Kill Criterion */}
        {killCriterion && (
          <div className="flex items-start gap-3 bg-destructive/5 rounded-lg p-3 border border-destructive/15">
            <Shield className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-destructive uppercase tracking-wide mb-1">Kill-Kriterium (Kipppunkt)</p>
              <p className="text-sm text-foreground">{typeof killCriterion === 'string' ? killCriterion : killCriterion.criterion || killCriterion.text || ''}</p>
            </div>
          </div>
        )}

        {/* Irreversibility Dashboard */}
        {irr && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Irreversibilitäts-Profil</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Structural Urgency Score */}
              {urgencyScoreVal != null && (
                <div className={cn("p-3 rounded-lg border", getUrgencyBg(urgencyScoreVal))}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Gauge className="w-3 h-3 text-muted-foreground" />
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Dringlichkeit</p>
                  </div>
                  <p className={cn("text-xl font-bold", getUrgencyColor(urgencyScoreVal))}>
                    {urgencyScoreVal}/10
                  </p>
                </div>
              )}

              {/* Point of No Return */}
              {irr.pivot_calendar_months != null && (
                <div className="p-3 rounded-lg border border-border/40 bg-muted/10">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Point of No Return</p>
                  </div>
                  <p className="text-xl font-bold text-foreground">
                    Monat {Number(irr.pivot_calendar_months).toFixed(1).replace('.', ',')}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    ≈ {Math.round(Number(irr.pivot_calendar_months) * 30)} Tage
                  </p>
                </div>
              )}

              {/* Daily Burn - only show if > 0 */}
              {irr.exposure_until_validation != null && Number(irr.exposure_until_validation) > 0 && (
                <div className="p-3 rounded-lg border border-border/40 bg-muted/10">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp className="w-3 h-3 text-muted-foreground" />
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Burn / Tag</p>
                  </div>
                  <p className="text-xl font-bold text-foreground">
                    {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(irr.exposure_until_validation)}
                  </p>
                </div>
              )}

              {/* Pivot Cost - only show if > 0 */}
              {irr.pivot_cost_eur != null && Number(irr.pivot_cost_eur) > 0 && (
                <div className="p-3 rounded-lg border border-border/40 bg-muted/10">
                  <div className="flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="w-3 h-3 text-muted-foreground" />
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Pivot-Kosten</p>
                  </div>
                  <p className="text-xl font-bold text-foreground">
                    {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(irr.pivot_cost_eur)}
                  </p>
                </div>
              )}
            </div>

            {/* Lock-in Types */}
            {irr.lock_in_types && Array.isArray(irr.lock_in_types) && irr.lock_in_types.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground mr-1">Lock-in:</span>
                {irr.lock_in_types.map((type: string, i: number) => (
                  <Badge key={i} variant="outline" className="text-[10px]">{type}</Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Impact / Urgency + Tension Score */}
        {(impactScore != null || urgencyScore != null || conflict) && (
          <TooltipProvider>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {impactScore != null && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="p-3 rounded-lg border border-border/40 bg-muted/10 cursor-help">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Target className="w-3 h-3 text-muted-foreground" />
                        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Impact</p>
                        <Info className="w-2.5 h-2.5 text-muted-foreground ml-auto" />
                      </div>
                      <p className="text-lg font-bold text-foreground">{impactScore}/10</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs">Berechnet aus Kapazitätsbindung, KPI-Verschiebung und Irreversibilitätsgrad der Entscheidung.</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {urgencyScore != null && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="p-3 rounded-lg border border-border/40 bg-muted/10 cursor-help">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Urgency</p>
                        <Info className="w-2.5 h-2.5 text-muted-foreground ml-auto" />
                      </div>
                      <p className="text-lg font-bold text-foreground">{urgencyScore}/10</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs">Basiert auf der Validierungsdeadline, dem Point of No Return und dem strukturellen Zeitdruck.</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {conflict?.tension_score != null && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="p-3 rounded-lg border border-border/40 bg-muted/10 cursor-help">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Gauge className="w-3 h-3 text-muted-foreground" />
                        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Tension Score</p>
                        <Info className="w-2.5 h-2.5 text-muted-foreground ml-auto" />
                      </div>
                      <p className="text-lg font-bold text-foreground">{conflict.tension_score}/10</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs">Misst die Intensität des strategischen Zielkonflikts zwischen den beiden konkurrierenden Strategien.</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        )}

        {/* Worst-Case Scenario */}
        {worstCase && (
          <div className="bg-amber-500/5 rounded-lg p-3 border border-amber-500/15">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide">Worst-Case Szenario</p>
              {worstCaseProb != null && (
                <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
                  {worstCaseProb}% Wahrscheinlichkeit
                </Badge>
              )}
            </div>
            <p className="text-sm text-foreground">{worstCase}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}